import VueRouter from 'vue-router';
import { RouteName } from '../../../routes/routes.constants';
import { magic } from '@gig/magic-sdk';
import { modalService } from '@gig/magic-common';
import {
    USER_JURISDICTION_QUERY,
    USER_PROFILE_DATA,
} from '@gig/magic-messages';
import { IUserProfile } from '@gig/magic-sdk/dist/adapter/users/user';
import { isRouteActive } from '../helpers/routes';
import { isUKGC, Jurisdiction } from '@gig/magic-jurisdictions';

export function initPeriodicAccountReviewGuard(router: VueRouter) {
    router.beforeEach(async (to, from, next) => {
        if (!magic.session.getUser() || to.name === RouteName.LOGOUT) {
            return next();
        }

        const currentJurisdiction = await window.queryBus.query<Jurisdiction>(
            USER_JURISDICTION_QUERY,
        )!;
        if (!isUKGC(currentJurisdiction)) {
            return next();
        }

        if (isRouteActive(RouteName.SET_LIMITS, to, router)) {
            return next();
        }

        const profile = await window.queryBus.query<IUserProfile | null>(
            USER_PROFILE_DATA,
        );

        if (!profile?.consents.lastAccountReviewInterval) {
            try {
                const nowIso = new Date().toISOString();
                await magic.methods.users.updateUserProfile({
                    lastAccountReviewInterval: 'P6M',
                    lastAccountReviewOnUtc: nowIso,
                });
            } catch (err) {
                console.error('Failed to set default interval:', err);
            }
            openModal();
            return next();
        } else if (shouldShowPeriodicReviewModal(profile)) {
            openModal();
            return next();
        }

        next();
    });
}

function monthsFromInterval(interval: string | null): number {
    if (!interval) {
        return 6;
    }

    const match = interval.match(/P(\d+)M/);
    return match ? parseInt(match[1], 10) : 6;
}

function shouldShowPeriodicReviewModal(profile: any): boolean {
    const intervalMonths = monthsFromInterval(
        profile.consents?.lastAccountReviewInterval,
    );
    const lastReviewDate = profile.consents?.lastAccountReviewOnUtc
        ? new Date(profile.consents.lastAccountReviewOnUtc)
        : null;

    // Case 1: No interval set yet → show prompt to choose period
    if (!profile.consents?.lastAccountReviewInterval) {
        return true;
    }

    // Case 2: Interval exists but no review date yet → show modal immediately
    if (!lastReviewDate) {
        return true;
    }

    // Case 3: Check if interval has elapsed
    const nextReviewDate = new Date(lastReviewDate);
    nextReviewDate.setMonth(nextReviewDate.getMonth() + intervalMonths);

    return new Date() >= nextReviewDate;
}

async function openModal() {
    const modal = document.createElement(
        'magic-corex-periodic-account-review-modal',
    );
    const modalHeader = 'corex-periodic-account-review-modal';
    const modalOptions = {
        dismissible: false,
        dismissibleBackdrop: false,
        displayOnTop: true,
        keepAfterClear: true,
    };

    modalService.open(modalHeader, modal, modalOptions);

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    modal.addEventListener('close', () => {});
}
